import React, { useState } from "react";
import { ADMIN_STATS, ADMIN_USERS, ADMIN_COMPANIES, JOBS } from "./data";
import { Chip, Icon, Logo, Avatar } from "./ui";

export default function AdminApp({ onExit }) {
  const [nav, setNav] = useState("dashboard");
  const [users, setUsers] = useState(ADMIN_USERS);
  const [companies, setCompanies] = useState(ADMIN_COMPANIES);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSuspend = (id) => setUsers(users.map(u => u.id === id ? { ...u, status: u.status === "suspended" ? "active" : "suspended" } : u));
  const toggleVerify = (id) => setCompanies(companies.map(c => c.id === id ? { ...c, status: c.status === "verified" ? "pending" : "verified" } : c));

  return (
    <div className="min-h-screen grain relative">
      <div className="flex min-h-screen relative z-10">
        {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)}/>}
        <aside className={`w-64 border-r border-[var(--line)] bg-[var(--ink)] text-[var(--paper)] p-4 flex flex-col z-40 fixed inset-y-0 left-0 lg:sticky lg:top-0 lg:translate-x-0 lg:h-screen transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
          style={{minHeight: "100vh"}}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[var(--brand-2)] grid place-items-center font-display font-bold text-[var(--ink)]">tx</div>
            <div className="font-display leading-none">
              <div className="font-bold">TalentXpres</div>
              <div className="text-xs text-white/60">Admin console</div>
            </div>
          </div>
          <nav className="mt-8 space-y-1 flex-1">
            {[
              {k:"dashboard", l:"Overview", i:<Icon.Home/>},
              {k:"users", l:"Users", i:<Icon.Users/>},
              {k:"companies", l:"Companies", i:<Icon.Building/>},
              {k:"jobs", l:"Job moderation", i:<Icon.Briefcase/>},
              {k:"payments", l:"Payments", i:<Icon.Money/>},
              {k:"tickets", l:"Support tickets", i:<Icon.Bell/>},
              {k:"config", l:"Config", i:<Icon.Settings/>},
            ].map(x => (
              <div key={x.k} data-testid={`admin-nav-${x.k}`} onClick={() => { setNav(x.k); setSidebarOpen(false); }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm font-medium ${nav===x.k ? "bg-[var(--brand-2)] text-[var(--ink)]" : "text-white/70 hover:bg-white/5"}`}>
                {x.i} {x.l}
              </div>
            ))}
          </nav>
          <button onClick={onExit} className="btn text-xs border" style={{color:"white", borderColor:"rgba(255,255,255,0.2)", background:"transparent"}}>← All prototypes</button>
        </aside>

        <main className="flex-1 max-w-full min-w-0">
          <div className="border-b border-[var(--line)] bg-white px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2" aria-label="Menu">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
              <div className="font-display text-base md:text-lg">Admin dashboard</div>
              <Chip tone="dark">Super Admin</Chip>
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <div className="text-xs text-[var(--muted)] hidden md:inline">admin@talentxpres.com</div>
              <Avatar name="Platform Admin" size={36}/>
            </div>
          </div>
          <div className="p-4 md:p-8 max-w-7xl">
            {nav === "dashboard" && <Overview/>}
            {nav === "users" && <UsersTable users={users} onSuspend={toggleSuspend}/>}
            {nav === "companies" && <CompaniesTable companies={companies} onVerify={toggleVerify}/>}
            {nav === "jobs" && <JobsModeration/>}
            {nav === "payments" && <Payments/>}
            {nav === "tickets" && <Tickets/>}
            {nav === "config" && <Config/>}
          </div>
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const cards = [
    { l: "Total candidates", v: ADMIN_STATS.candidates.toLocaleString(), d: "+248 this week" },
    { l: "Employers", v: ADMIN_STATS.employers, d: "+9 this week" },
    { l: "Verified companies", v: ADMIN_STATS.companies, d: `${ADMIN_STATS.pendingVerifications} pending` },
    { l: "Active jobs", v: ADMIN_STATS.activeJobs.toLocaleString() },
    { l: "Applications (total)", v: ADMIN_STATS.applications.toLocaleString() },
    { l: "Hires closed", v: ADMIN_STATS.hires, d: "avg 19d cycle" },
    { l: "Open tickets", v: ADMIN_STATS.openTickets },
    { l: "MRR", v: "₹18.4L", d: "+₹1.2L MoM" },
  ];
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Platform overview</h1>
      <p className="text-[var(--ink-2)] mt-1">All eyes on 12 pending company verifications.</p>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.l} className="card p-5">
            <div className="text-xs text-[var(--muted)]">{c.l}</div>
            <div className="font-display text-3xl mt-1">{c.v}</div>
            {c.d && <div className="text-xs text-[var(--brand)] mt-1">{c.d}</div>}
          </div>
        ))}
      </div>
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-display text-xl">Signups — last 30 days</h3>
          <div className="mt-4 flex gap-1 h-40 items-end">
            {[12,18,15,22,28,24,30,26,34,40,38,45,52,48,55,60,58,64,70,66,74,80,76,82,88,92,90,96,102,108].map((v,i) => (
              <div key={i} className="flex-1 rounded-t bg-[var(--brand)]" style={{height:`${v}%`, opacity: 0.4 + (i/60)}}/>
            ))}
          </div>
        </div>
        <div className="card p-6">
          <h3 className="font-display text-xl">Availability distribution</h3>
          <div className="mt-4 space-y-3">
            {[
              {l:"Immediate joiners", v:2830, w:70, c:"var(--brand-2)"},
              {l:"≤15 days", v:1620, w:45, c:"#5FCE9A"},
              {l:"16–45 days", v:4210, w:100, c:"#F5B849"},
              {l:"46–90 days", v:2820, w:65, c:"#FF6A3D"},
            ].map(r => (
              <div key={r.l} className="flex items-center gap-3">
                <div className="w-36 text-sm">{r.l}</div>
                <div className="flex-1 h-4 bg-[var(--paper-2)] rounded"><div className="h-full rounded" style={{width:`${r.w}%`, background: r.c}}/></div>
                <div className="w-14 text-right text-sm font-semibold">{r.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTable({ users, onSuspend }) {
  const [filter, setFilter] = useState("all");
  const list = filter === "all" ? users : users.filter(u => u.role === filter || u.status === filter);
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Users</h1>
      <div className="mt-4 flex gap-2 flex-wrap">
        {["all", "candidate", "employer", "pending", "suspended"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border capitalize ${filter===f?"bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]":"bg-white border-[var(--line)]"}`}>{f}</button>
        ))}
      </div>
      <div className="mt-6 card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
            <tr><th className="px-6 py-3">Name</th><th className="px-6 py-3">Role</th><th className="px-6 py-3">Company</th><th className="px-6 py-3">Joined</th><th className="px-6 py-3">Status</th><th className="px-6 py-3"></th></tr>
          </thead>
          <tbody>
            {list.map(u => (
              <tr key={u.id} className="border-t border-[var(--line)] hover:bg-[var(--paper-2)]/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3"><Avatar name={u.name} size={32}/>
                    <div><div className="font-medium">{u.name}</div><div className="text-xs text-[var(--muted)]">{u.email}</div></div>
                  </div>
                </td>
                <td className="px-6 py-4 capitalize">{u.role}</td>
                <td className="px-6 py-4 text-[var(--ink-2)]">{u.company || "—"}</td>
                <td className="px-6 py-4 text-[var(--muted)]">{u.joined}</td>
                <td className="px-6 py-4">
                  <Chip tone={u.status==="active"?"mint":u.status==="suspended"?"accent":"default"}>{u.status}</Chip>
                </td>
                <td className="px-6 py-4 text-right">
                  <button data-testid={`suspend-${u.id}`} onClick={() => onSuspend(u.id)} className="text-xs text-[var(--brand)] font-semibold hover:underline">
                    {u.status==="suspended"?"Reinstate":"Suspend"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompaniesTable({ companies, onVerify }) {
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Companies</h1>
      <p className="text-[var(--ink-2)] mt-1">Approve or reject company verification requests.</p>
      <div className="mt-6 card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
            <tr><th className="px-6 py-3">Company</th><th className="px-6 py-3">Website</th><th className="px-6 py-3">GST/CIN</th><th className="px-6 py-3">Active jobs</th><th className="px-6 py-3">Status</th><th className="px-6 py-3"></th></tr>
          </thead>
          <tbody>
            {companies.map(c => (
              <tr key={c.id} className="border-t border-[var(--line)]">
                <td className="px-6 py-4 font-medium">{c.name}</td>
                <td className="px-6 py-4 text-[var(--brand)]">{c.website}</td>
                <td className="px-6 py-4 font-mono text-xs">{c.gst}</td>
                <td className="px-6 py-4">{c.jobs}</td>
                <td className="px-6 py-4"><Chip tone={c.status==="verified"?"mint":"accent"}>{c.status}</Chip></td>
                <td className="px-6 py-4 text-right">
                  <button data-testid={`verify-${c.id}`} onClick={() => onVerify(c.id)} className="text-xs text-[var(--brand)] font-semibold hover:underline">
                    {c.status === "verified" ? "Unverify" : "Approve"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobsModeration() {
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Job moderation</h1>
      <div className="mt-6 space-y-3">
        {JOBS.map(j => (
          <div key={j.id} className="card p-4 flex items-center justify-between">
            <div>
              <div className="font-semibold">{j.title}</div>
              <div className="text-xs text-[var(--muted)]">{j.company} · {j.location} · Posted {j.postedAgo}</div>
              <div className="mt-2 flex gap-1.5"><Chip>{j.applicants} applicants</Chip><Chip tone="accent">≤{j.maxJoiningDays}d</Chip></div>
            </div>
            <div className="flex gap-2">
              <button className="btn btn-ghost text-xs">Flag</button>
              <button className="btn btn-ghost text-xs" style={{color:"var(--danger)"}}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Payments() {
  const rows = [
    { d:"12 Jan 2026", c:"AcmeCorp Technologies", p:"Pro", a:"₹14,999", s:"paid" },
    { d:"11 Jan 2026", c:"BrightPath EdTech", p:"Pro", a:"₹14,999", s:"paid" },
    { d:"09 Jan 2026", c:"NovaHealth", p:"Free→Pro", a:"₹14,999", s:"paid" },
    { d:"08 Jan 2026", c:"Stealth.io", p:"Pro", a:"₹14,999", s:"failed" },
    { d:"05 Jan 2026", c:"HyperNode", p:"Enterprise", a:"₹49,999", s:"paid" },
  ];
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Payments</h1>
      <div className="mt-4 grid grid-cols-3 gap-4">
        {[{l:"MRR",v:"₹18.4L"},{l:"Successful (30d)",v:"₹22.8L"},{l:"Failed (30d)",v:"₹34K"}].map(x => (
          <div key={x.l} className="card p-5"><div className="text-xs text-[var(--muted)]">{x.l}</div><div className="font-display text-3xl mt-1">{x.v}</div></div>
        ))}
      </div>
      <div className="mt-6 card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
            <tr><th className="px-6 py-3">Date</th><th className="px-6 py-3">Company</th><th className="px-6 py-3">Plan</th><th className="px-6 py-3">Amount</th><th className="px-6 py-3">Status</th></tr>
          </thead>
          <tbody>
            {rows.map((r,i)=>(<tr key={i} className="border-t border-[var(--line)]"><td className="px-6 py-3">{r.d}</td><td className="px-6 py-3">{r.c}</td><td className="px-6 py-3">{r.p}</td><td className="px-6 py-3 font-semibold">{r.a}</td><td className="px-6 py-3"><Chip tone={r.s==="paid"?"mint":"accent"}>{r.s}</Chip></td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tickets() {
  const ts = [
    { id:"T-2041", u:"Rahul Iyer", s:"Cannot upload resume PDF > 3MB", p:"medium", t:"2h ago" },
    { id:"T-2040", u:"Priya Sharma", s:"Razorpay charged twice", p:"high", t:"5h ago" },
    { id:"T-2039", u:"Sneha Rao", s:"Update phone number", p:"low", t:"1d ago" },
    { id:"T-2038", u:"AcmeCorp HR", s:"Kanban card duplicating", p:"medium", t:"2d ago" },
  ];
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Support tickets</h1>
      <div className="mt-6 space-y-2">
        {ts.map(t=>(
          <div key={t.id} className="card p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="font-mono text-xs text-[var(--muted)]">{t.id}</div>
              <div><div className="font-medium">{t.s}</div><div className="text-xs text-[var(--muted)]">{t.u} · {t.t}</div></div>
            </div>
            <Chip tone={t.p==="high"?"accent":"default"}>{t.p}</Chip>
          </div>
        ))}
      </div>
    </div>
  );
}

function Config() {
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Platform config</h1>
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="font-display text-xl">Skill library</h3>
          <p className="text-xs text-[var(--muted)] mt-1">Standardized skills shown to candidates and employers.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["Python","FastAPI","React","Node.js","Kubernetes","AWS","Figma","UX Research","PostgreSQL","MongoDB","Machine Learning"].map(s => <Chip key={s} tone="mint">{s}</Chip>)}
            <button className="px-2.5 py-1 rounded-full text-xs border border-dashed border-[var(--line)]">+ Add skill</button>
          </div>
        </div>
        <div className="card p-6">
          <h3 className="font-display text-xl">Cities live</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {["Mumbai","Delhi","Bangalore","Hyderabad","Chennai","Pune","Remote"].map(c => <Chip key={c}>{c}</Chip>)}
          </div>
        </div>
        <div className="card p-6 lg:col-span-2">
          <h3 className="font-display text-xl">Plan pricing (INR)</h3>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-[var(--paper-2)]"><div className="text-xs text-[var(--muted)]">Free</div><div className="font-display text-2xl">₹0</div></div>
            <div className="p-4 rounded-xl bg-[var(--paper-2)]"><div className="text-xs text-[var(--muted)]">Pro / mo</div><div className="font-display text-2xl">₹14,999</div></div>
            <div className="p-4 rounded-xl bg-[var(--paper-2)]"><div className="text-xs text-[var(--muted)]">Enterprise / mo</div><div className="font-display text-2xl">₹49,999</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
